import { isEmpty, isEqual } from 'lodash';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { usePrevious, useAsyncEffect } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useFromMenuUrlParams from 'hb-react/shared/hooks/useFromMenuUrlParams';

import useRealV0FiltersTopics from './useRealV0FiltersTopics';
import initialFilters from '../constants/initialFilters';
import { ITEMS_PER_ROWS_DEFAULT } from '../constants/itemsPerRow';
import {
  PARAM_CATEGORY_ID,
  PARAM_EXCLUDE_FOLLOWED,
  PARAM_FOLLOWED_BY_USER,
  PARAM_OTHER_TOPICS,
  PARAM_SUGGESTED_FOR_USER,
  PARAM_SUPER_CATEGORY_ID,
} from '../constants/queryParams';
import {
  CATEGORIES,
  CATEGORY,
  FILTERED,
  MY_TOPICS,
  OTHER_TOPICS,
  SUGGESTED_TOPICS,
  SUPER_CATEGORY,
} from '../constants/types';
import fetchCategories from '../requests/fetchCategories';
import getFiltersAppConfig from '../utils/getFiltersAppConfig';
import reduceParams from '../utils/reduceParams';

const usedQueryParams = [
  PARAM_CATEGORY_ID,
  PARAM_EXCLUDE_FOLLOWED,
  PARAM_FOLLOWED_BY_USER,
  PARAM_OTHER_TOPICS,
  PARAM_SUGGESTED_FOR_USER,
  PARAM_SUPER_CATEGORY_ID,
];

const usePrepareFiltersAppConfig = (
  setFilters,
  isConnectedUser,
  categories,
  setCategoryView,
) =>
  getFiltersAppConfig({
    setFilters,
    staticFilters: useRealV0FiltersTopics({
      isConnectedUser,
      categories,
      setCategoryView,
    }),
    ignoredParamsKeys: usedQueryParams,
  });

const getIsEnabledToPublishSuperCategories = (flags, currentNetwork) =>
  !!Number(currentNetwork?.features?.topics?.config?.category_types) &&
  currentNetwork?.features?.topics?.config?.super_categories_group_enabled;

const handleSetCategoryView =
  (setSelectedCategoryId, setSelectedSuperCategoryId, setSearchParams) =>
  (categoryId, superCategoryId) => {
    setSelectedCategoryId(categoryId);
    setSelectedSuperCategoryId(superCategoryId);

    // Change URL to category one - clean all other URL params
    setSearchParams([[PARAM_CATEGORY_ID, categoryId]]);
  };

const handleSetMyTopicsView = (setCurrentView, setSearchParams) => {
  setCurrentView(MY_TOPICS);

  // Change URL to my_topics one - clean all other URL params
  setSearchParams([[PARAM_FOLLOWED_BY_USER, 'true']]);
};

const handleSetSuggestedTopicsView = (setCurrentView, setSearchParams) => {
  setCurrentView(SUGGESTED_TOPICS);

  // Change URL to suggested topics one - clean all other URL params
  setSearchParams([[PARAM_SUGGESTED_FOR_USER, 'true']]);
};

const handleSetCategoriesView = (setCurrentView, setSearchParams) => {
  setCurrentView(CATEGORIES);

  // Change URL to topics index page - clean all other URL params
  setSearchParams([]);
};

const handleSetSuperCategoryView =
  (setSelectedSuperCategoryId, setCurrentView, setSearchParams) =>
  (superCategoryId) => {
    setSelectedSuperCategoryId(superCategoryId);

    setCurrentView(SUPER_CATEGORY);

    // Change URL to super category one - clean all other URL params
    setSearchParams([[PARAM_SUPER_CATEGORY_ID, superCategoryId]]);
  };

const handleInitialView = (
  searchParams,
  setCategoryView,
  setMyTopicsView,
  setSuggestedTopicsView,
  setCurrentView,
  setSuperCategoryView,
) => {
  if (searchParams.get(PARAM_CATEGORY_ID)) {
    setCategoryView(searchParams.get(PARAM_CATEGORY_ID));
  } else if (searchParams.get(PARAM_FOLLOWED_BY_USER)) {
    setMyTopicsView();
  } else if (searchParams.get(PARAM_SUGGESTED_FOR_USER)) {
    setSuggestedTopicsView();
  } else if (searchParams.get(PARAM_OTHER_TOPICS)) {
    setCurrentView(OTHER_TOPICS);
  } else if (searchParams.get(PARAM_SUPER_CATEGORY_ID)) {
    setSuperCategoryView(searchParams.get(PARAM_SUPER_CATEGORY_ID));
  } else {
    setCurrentView(CATEGORIES);
  }
};

const useTopicsIndexApp = () => {
  const breakpoint = useBreakpoint();
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const flags = useFeatureFlag();
  const { shouldUpdateFromMenu } = useFromMenuUrlParams({
    includeKeys: usedQueryParams,
  });

  const [filters, setFilters] = useState({ query: initialFilters });
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedSuperCategoryId, setSelectedSuperCategoryId] = useState(null);
  const [selectedSuperCategory, setSelectedSuperCategory] = useState(null);
  const [totalAllTopics, setTotalAllTopics] = useState({});
  const [areUsingFilters, setAreUsingFilters] = useState(false);
  const [handleFetchMyTopics, setHandleFetchMyTopics] = useState(null);
  const [handleFetchSuggestedTopics, setHandleFetchSuggestedTopics] =
    useState(null);
  const [handleFetchOtherTopics, setHandleFetchOtherTopics] = useState(null);
  const [handleFetchFilteredTopics, setHandleFetchFilteredTopics] =
    useState(null);
  const [handleFetchCategoriesTopics, setHandleFetchCategoriesTopics] =
    useState({});
  const [areFetchedAllCategories, setAreFetchedAllCategories] = useState(false);
  const [totalAllCategories, setTotalAllCategories] = useState(0);
  const [
    displaySuccessMessageJoinPublicTopic,
    setDisplaySuccessMessageJoinPublicTopic,
  ] = useState({});
  const [currentView, setCurrentView] = useState();
  const [searchParams, setSearchParams] = useSearchParams();
  const isConnectedUser = !!currentUser;

  const filtersAppRef = useRef();
  const initialRender = useRef(true);

  const prevParams = usePrevious(reduceParams(filters.query));

  const itemsPerRow = ITEMS_PER_ROWS_DEFAULT[breakpoint];

  const isEnabledToPublishSuperCategories =
    getIsEnabledToPublishSuperCategories(flags, currentNetwork);

  const handleResetFilters = useCallback(() => {
    if (filtersAppRef?.current) {
      filtersAppRef.current.resetFilters();
    }
  }, [filtersAppRef]);

  const fetchMyTopics = useCallback(() => {
    if (handleFetchMyTopics) {
      handleFetchMyTopics({ page: 1 });
    }
  }, [handleFetchMyTopics]);

  const fetchSuggestedTopics = useCallback(() => {
    if (handleFetchSuggestedTopics) {
      handleFetchSuggestedTopics({ page: 1 });
    }
  }, [handleFetchSuggestedTopics]);

  const fetchOtherTopics = useCallback(() => {
    if (handleFetchOtherTopics) {
      handleFetchOtherTopics({ page: 1 });
    }
  }, [handleFetchOtherTopics]);

  const fetchFilteredTopics = useCallback(() => {
    if (handleFetchFilteredTopics) {
      const query = reduceParams(filters.query);

      handleFetchFilteredTopics({ page: 1, ...query });
    }
  }, [handleFetchFilteredTopics, filters]);

  const pushHandleFetchCategoryTopics = useCallback(
    (categoryId, handleFetchFunction) => {
      setHandleFetchCategoriesTopics((prevHandleFetchCategoriesTopics) => ({
        ...prevHandleFetchCategoriesTopics,
        [categoryId]: handleFetchFunction,
      }));
    },
    [],
  );

  const fetchCategoryTopics = useCallback(
    (categoryId) => {
      if (handleFetchCategoriesTopics[categoryId]) {
        handleFetchCategoriesTopics[categoryId]({ page: 1 });
      }
    },
    [handleFetchCategoriesTopics],
  );

  const setCategoryView = handleSetCategoryView(
    setSelectedCategoryId,
    setSelectedSuperCategoryId,
    setSearchParams,
  );

  const setMyTopicsView = () =>
    handleSetMyTopicsView(setCurrentView, setSearchParams);

  const setSuggestedTopicsView = () =>
    handleSetSuggestedTopicsView(setCurrentView, setSearchParams);

  const setCategoriesView = () =>
    handleSetCategoriesView(setCurrentView, setSearchParams);

  const setSuperCategoryView = handleSetSuperCategoryView(
    setSelectedSuperCategoryId,
    setCurrentView,
    setSearchParams,
  );

  const filtersAppConfig = usePrepareFiltersAppConfig(
    setFilters,
    isConnectedUser,
    categories,
    setCategoryView,
  );

  // Set initial view - listen to URL params from prefiltered URL
  useEffect(() => {
    handleInitialView(
      searchParams,
      setCategoryView,
      setMyTopicsView,
      setSuggestedTopicsView,
      setCurrentView,
      setSuperCategoryView,
    );
  }, []);

  // Handle view changes from custom menu link updates
  useEffect(() => {
    if (shouldUpdateFromMenu()) {
      handleInitialView(
        searchParams,
        setCategoryView,
        setMyTopicsView,
        setSuggestedTopicsView,
        setCurrentView,
        setSuperCategoryView,
      );
    }
  }, [searchParams]);

  // Handle view changes when filter is used by the user
  useEffect(() => {
    const params = reduceParams(filters.query);

    setAreUsingFilters(!isEmpty(params));

    if (prevParams && !isEqual(prevParams, params)) {
      if (isEmpty(params)) {
        // We don't set CATEGORIES view if the user just select a category
        if (!selectedCategoryId) {
          // If the user clean the filters, we go back to categories/index view
          setCurrentView(CATEGORIES);
        }
      } else {
        // We switch to the FILTERED view if filters are used
        setCurrentView(FILTERED);
      }
    }
  }, [filters]);

  // Categories fetch for filter dropdown
  useAsyncEffect(async () => {
    const response = await fetchCategories({
      per_page: 200,
      categories_with_visible_topics: true,
    });

    setCategories(response?.categories);
  }, []);

  // Clean internal categories or filters based on view changes
  // My groups & Category are exclusive from filters (can't set both at the same time)
  useEffect(() => {
    // Reset selected super category if we're in the index page
    if (currentView === CATEGORIES) {
      setSelectedSuperCategoryId(null);
      setSelectedSuperCategory(null);
    }

    // User has selected a category, clean filters
    if (currentView === CATEGORY) {
      handleResetFilters();
    } else {
      // After first render, reset selected category if we're using another view
      if (!initialRender.current) {
        setSelectedCategoryId(null);
        setSelectedCategory(null);
      }

      initialRender.current = false;

      if (
        currentView === OTHER_TOPICS ||
        currentView === MY_TOPICS ||
        currentView === CATEGORIES ||
        currentView === SUGGESTED_TOPICS
      ) {
        handleResetFilters();
      }
    }
  }, [currentView]);

  return {
    areFetchedAllCategories,
    areUsingFilters,
    currentView,
    displaySuccessMessageJoinPublicTopic,
    fetchCategoryTopics,
    fetchMyTopics,
    fetchOtherTopics,
    fetchFilteredTopics,
    fetchSuggestedTopics,
    filters,
    filtersAppConfig,
    filtersAppRef,
    handleResetFilters,
    isConnectedUser,
    isEnabledToPublishSuperCategories,
    itemsPerRow,
    pushHandleFetchCategoryTopics,
    selectedCategory,
    selectedCategoryId,
    selectedSuperCategory,
    selectedSuperCategoryId,
    setAreFetchedAllCategories,
    setCategoriesView,
    setCategoryView,
    setCurrentView,
    setDisplaySuccessMessageJoinPublicTopic,
    setHandleFetchMyTopics,
    setHandleFetchOtherTopics,
    setHandleFetchFilteredTopics,
    setHandleFetchSuggestedTopics,
    setMyTopicsView,
    setSelectedCategory,
    setSelectedCategoryId,
    setSelectedSuperCategory,
    setSelectedSuperCategoryId,
    setSuggestedTopicsView,
    setSuperCategoryView,
    setTotalAllCategories,
    setTotalAllTopics,
    totalAllCategories,
    totalAllTopics,
  };
};

export default useTopicsIndexApp;
